import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useCallback, useState, useRef } from 'react';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    googleReCaptchaOnLoad: () => void;
  }
}

export type HCaptchaErrorStatus = 'error' | 'expired' | 'timeout';
export type HCaptchaStatus = 'loading' | 'idle' | 'busy' | 'success' | HCaptchaErrorStatus;
export type HCaptchaService = 'wolt-auth' | 'mos';

const getSiteKeyForService = (service: HCaptchaService): string => {
  switch (service) {
    case 'wolt-auth':
      return process.env.WOLT_AUTH_HCAPTCHA_SITE_KEY;
    case 'mos':
      return process.env.MOS_HCAPTCHA_SITE_KEY;
  }
};

export const isHCaptchaErrorStatus = (status: HCaptchaStatus): status is HCaptchaErrorStatus =>
  ['error', 'timeout', 'expired'].includes(status);

const H_CAPTCHA_TIMEOUT = 20000;

const useHCaptcha = (service: HCaptchaService) => {
  const captchaRef = useRef<HCaptcha>(null);
  const [hCaptchaStatus, setHCaptchaStatus] = useState<HCaptchaStatus>('loading');

  const sitekey = getSiteKeyForService(service);

  const hCaptchaHandlers = {
    onLoad: () => setHCaptchaStatus('idle'),
    onError: () => setHCaptchaStatus('error'),
    onChalExpired: () => setHCaptchaStatus('expired'),
    onVerify: () => setHCaptchaStatus('success'),
  };

  const runHCaptcha = useCallback(async () => {
    setHCaptchaStatus('busy');

    if (!captchaRef.current) {
      throw new Error('Could not find HCaptcha element');
    }

    const checkTimeout = new Promise((_, reject) =>
      setTimeout(() => {
        setHCaptchaStatus('timeout');
        reject('hCaptcha timed out');
      }, H_CAPTCHA_TIMEOUT),
    );

    const captchaExecuteResponse = captchaRef.current.execute({
      async: true,
    });

    // captchaRef.current.execute never settles in some cases (for instance on localhost).
    // None of the handlers is executed in that case, causing the status to remain 'busy'.
    // This race ensures that the status is changed to 'timeout' in that case.
    const { response } = await (Promise.race([captchaExecuteResponse, checkTimeout]) as typeof captchaExecuteResponse);

    return response;
  }, []);

  const runWithHCaptcha = async <T>(callback: (reCaptchaResult: string) => Promise<T>) => {
    const result = await runHCaptcha();

    if (result) {
      return await callback(result);
    }

    throw new Error('HCaptcha verification failed');
  };

  return { sitekey, captchaRef, hCaptchaStatus, runWithHCaptcha, hCaptchaHandlers };
};

const useFakeHCaptcha = (_: HCaptchaService) => {
  const [hCaptchaStatus, setHCaptchaStatus] = useState<HCaptchaStatus>('loading');

  const hCaptchaHandlers = {
    onLoad: () => setHCaptchaStatus('idle'),
    onError: () => setHCaptchaStatus('error'),
    onChalExpired: () => setHCaptchaStatus('expired'),
    onVerify: () => setHCaptchaStatus('success'),
  };

  const runWithHCaptcha = async <T>(callback: (reCaptchaResult: string) => Promise<T>) => {
    hCaptchaHandlers.onVerify();
    return await callback('fake-recaptcha-token');
  };

  return {
    sitekey: 'fake-site-key',
    captchaRef: null,
    hCaptchaStatus,
    runWithHCaptcha,
    hCaptchaHandlers,
  };
};

const isProduction = process.env.APP_ENV === 'production' && process.env.NODE_ENV === 'production';

export default isProduction ? useHCaptcha : useFakeHCaptcha;
